import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { Box, Grid, Typography } from "@mui/material";
import "../../components-styles.scss";
import "../../Dossiers/Dossier/dossier-styles.scss";
import {
    OverViewWidgetItem,
    Session,
    DossierStatusEnum,
    Dossier,
    ClientDashBoardNavBarMenuItemId,
    DossierDetailType,
} from "../../../types";
import ReactSvgIcon from "../../UIElements/ReactSvgIcon";
import { ReactComponent as DossierAttente } from "../../../public/icons/DossierAttente.svg";
import { ReactComponent as DossierEdit } from "../../../public/icons/DossierEdit.svg";
import { ReactComponent as DossierReload } from "../../../public/icons/DossierReload.svg";
import OverViewWidgets from "../../OverViewWigets/OverViewWidgets";
import HomeHeader from "../../Common/HomeHeader/HomeHeader";
import { useDossier } from "../../Dossiers/useDossier";
import OverViewDossierWrapper from "../../Dossiers/Dossier/OverViews/OverViewDossierWrapper";
import { sessionStorageKey } from "../../../consts";
import { useLocalStorage } from "../../../hooks";
import { getDossiers } from "../../../services/dossier.service";
import { getDossierStatus } from "../../../services/dossier.service";
import { getDossierStatusByCode } from "../../Dossiers/utils";
import Dossiers from "../../Dossiers/Dossiers";
import ExportCsvBtn from "../../UIElements/ExportCsvBtn";
import { exportCsv } from "../../../helpers";
import { LayoutBarContext } from "../../../contexts/LayoutBarContext";
import { sortAscending, sortDescending } from "../../utils";
import CustomSwapVert from "../../UIElements/CustomSwapVert";

export default function Home() {
    const { storedValue: session } =
        useLocalStorage<Session>(sessionStorageKey);

    const {
        auth: { user: currentUser },
    } = session;

    const counterStruct: any = {
        attente: 0,
        attenteExam: 0,
        encoursExam: 0,
    };
    const { updateContent, setActive } = useContext(LayoutBarContext);
    const [dossiersCount, setDossiersCount] = useState(counterStruct);
    const [dossierStatuses, setDossierStatuses] = useState([]);
    const [isDescendingSort, setisDescendingSort] = useState<boolean>(true);

    const setUpDossierStatuses = useCallback(async () => {
        const statuses = await getDossierStatus();
        if (!statuses.detail) {
            setDossierStatuses(statuses);
        }
    }, []);
    useEffect(() => {
        setUpDossierStatuses();
    }, [setUpDossierStatuses]);

    const dossierCounter = useCallback(async () => {
        if (dossierStatuses.length > 0) {
            const allDossiers = await getDossiers();

            Object.values(DossierStatusEnum).forEach((code) => {
                const idStatus = (
                    getDossierStatusByCode(code, dossierStatuses) || {}
                ).id;

                switch (code) {
                    case DossierStatusEnum.ATTENTE_DECISION:
                        const dossiersAttenteDecision = allDossiers.filter(
                            (dossier: Dossier) => {
                                return dossier.dossierStatus == idStatus;
                            }
                        );
                        dossiersCount.attente = dossiersAttenteDecision.length;
                        dossiersCount.attenteExam =
                            dossiersAttenteDecision.filter(
                                (dossier: Dossier) => {
                                    return (
                                        dossier.assignedValidatorUser == null
                                    );
                                }
                            ).length;
                        dossiersCount.encoursExam =
                            dossiersAttenteDecision.filter(
                                (dossier: Dossier) => {
                                    return (
                                        dossier.assignedValidatorUser ==
                                        currentUser.id
                                    );
                                }
                            ).length;
                        break;
                    default:
                        break;
                }
            });
            setDossiersCount(dossiersCount);
        }
    }, [dossierStatuses, dossiersCount]);

    const {
        downloadCertificate,
        currentDossiers,
        _fetchCurrentDossiers,
        regionList,
    } = useDossier();
    useEffect(() => {
        dossierCounter();
    }, [dossierCounter]);

    const overViewWidgetItems: OverViewWidgetItem[] = useMemo(
        () => [
            {
                icon: (
                    <ReactSvgIcon
                        width="1.5rem"
                        height="1.5rem"
                        ReactIcon={DossierAttente}
                    />
                ),
                title: `${dossiersCount.attente} dossier(s) en attente de décision`,
                onClick: () => {
                    updateContent(<Dossiers />);
                    setActive(ClientDashBoardNavBarMenuItemId.DOSSIERS);
                },
            },
            {
                icon: (
                    <ReactSvgIcon
                        width="1.5rem"
                        height="1.5rem"
                        ReactIcon={DossierReload}
                    />
                ),
                title: `${dossiersCount.attenteExam} dossier(s) en attente d'examen`,
                onClick: () => {
                    updateContent(
                        <Dossiers
                            dossierStatusFilter={{
                                code: DossierStatusEnum.ATTENTE_DECISION,
                            }}
                        />
                    );
                    setActive(ClientDashBoardNavBarMenuItemId.DOSSIERS);
                },
            },
            {
                icon: (
                    <ReactSvgIcon
                        width="1.5rem"
                        height="1.5rem"
                        ReactIcon={DossierEdit}
                    />
                ),
                title: `${dossiersCount.encoursExam} dossier(s) en cours d'examen`,
                onClick: () => {
                    updateContent(
                        <Dossiers
                            dossierStatusFilter={{
                                code: DossierStatusEnum.ATTENTE_DECISION,
                            }}
                        />
                    );
                    setActive(ClientDashBoardNavBarMenuItemId.DOSSIERS);
                },
            },
        ],
        [dossiersCount.attente]
    );

    const currentDossiersSorted = useMemo(() => {
        if (!currentDossiers.length) {
            return currentDossiers;
        }
        if (isDescendingSort) {
            return sortDescending(
                currentDossiers,
                (d: DossierDetailType) => d.processedAt
            );
        } else {
            return sortAscending(
                currentDossiers,
                (d: DossierDetailType) => d.processedAt
            );
        }
    }, [currentDossiers, isDescendingSort]);

    const fetchCurrentDossiers = useCallback(async () => {
        await _fetchCurrentDossiers();
    }, []);

    useEffect(() => {
        fetchCurrentDossiers();
    }, [fetchCurrentDossiers]);

    return (
        <>
            <header className="dossier-header">
                <Box
                    sx={{
                        marginLeft: "6rem",
                        marginTop: "5vh",
                        marginRight: "1.5rem",
                        marginBottom: "2rem",
                        display: "flex",
                        flexDirection: "column",
                        gap: "2.28rem",
                        "@media (max-width:1024px)": {
                            marginLeft: "1.5rem",
                        },
                    }}
                >
                    <HomeHeader
                        profilName={
                            currentUser.firstName + " " + currentUser.lastName
                        }
                        title={"Bienvenue sur votre espace SYNERPA SOCOTEC"}
                    />
                </Box>
            </header>
            <main className="layout-body" style={{ overflow: "hidden" }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: "3rem",
                        marginLeft: "6rem",
                        marginRight: "6rem",
                        marginTop: "2rem",
                        width: "100%",
                        "@media (max-width:1024px)": {
                            marginRight: "1.5rem",
                            marginLeft: "1.5rem",
                            width: "100vh",
                            marginBottom: "5rem",
                        },
                        "@media (min-width: 600px) and (max-width:1024px)": {
                            width: "100vw",
                        },
                    }}
                >
                    <Grid container spacing={1.5}>
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "1.5rem",
                                width: "100%",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-end",
                                    alignSelf: "stretch",
                                    gap: "0.5rem",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: "Roboto",
                                        fontSize: "1.5rem",
                                        fontStyle: "normal",
                                        fontWeight: 500,
                                        lineHeight: "normal",
                                        color: "#023",
                                    }}
                                >
                                    Dossiers
                                </Typography>
                                <CustomSwapVert
                                    onClick={() => {
                                        setisDescendingSort(!isDescendingSort);
                                    }}
                                    rotate={isDescendingSort}
                                />
                            </Box>

                            {currentDossiers.length
                                ? currentDossiersSorted.map((cd: any) => {
                                      return (
                                          <OverViewDossierWrapper
                                              key={cd.id}
                                              dossierDetail={cd}
                                              downloadCertificate={
                                                  downloadCertificate
                                              }
                                              regionList={regionList}
                                          />
                                      );
                                  })
                                : null}
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "1.5rem",
                                width: "100%",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-end",
                                    alignSelf: "stretch",
                                    gap: "0.5rem",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: "Roboto",
                                        fontSize: "1.5rem",
                                        fontStyle: "normal",
                                        fontWeight: 500,
                                        lineHeight: "normal",
                                        color: "#023",
                                    }}
                                >
                                    Vue d’ensemble
                                </Typography>
                                <ExportCsvBtn
                                    onClick={async () => {
                                        await exportCsv();
                                    }}
                                />
                            </Box>
                            <OverViewWidgets
                                overViewWidgetItems={overViewWidgetItems}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </main>
        </>
    );
}
